import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import TabPageLayout from "./TabPageLayout";
import { LayoutActionButton } from "./Buttons";
import {
  Play, CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, ChevronRight, Clock, Bell, DatabaseBackup,
} from "lucide-react";
import { BACKEND_URL } from "../config/api";

const API = `${BACKEND_URL}/api/health/master`;

const statusMeta = {
  passed: { icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50 border-green-200" },
  warning: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  failed: { icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const MasterHealthPanel = () => {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [backup, setBackup] = useState(null);
  const [backingUp, setBackingUp] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("jwtToken")}` });

  const load = useCallback(async () => {
    try {
      const [l, h] = await Promise.all([
        axios.get(`${API}/latest`, { headers: authHeaders() }),
        axios.get(`${API}/history?limit=14`, { headers: authHeaders() }),
      ]);
      setLatest(l.data.run);
      setHistory(h.data.runs || []);
    } catch (e) {
      console.error("MasterHealth load failed", e);
    }
    try {
      const b = await axios.get(`${BACKEND_URL}/api/health/backup/status`, { headers: authHeaders() });
      setBackup(b.data);
    } catch (e) {
      console.error("Backup status load failed", e);
    }
  }, []);

  const runBackup = async () => {
    setBackingUp(true);
    toast.info("Backing up prod → S3 (read-only, ~30s)…");
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/health/backup/run`, {}, {
        headers: authHeaders(), timeout: 300000,
      });
      setBackup(data);
      toast.success(`Backup complete — ${data.totalRows?.toLocaleString()} rows across ${data.tableCount} tables`);
    } catch (e) {
      toast.error("Backup failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBackingUp(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const runAll = async () => {
    setRunning(true);
    toast.info("Running full test battery — this takes 2–4 minutes…");
    try {
      const { data } = await axios.post(`${API}/run?notify=false`, {}, {
        headers: authHeaders(), timeout: 600000,
      });
      setLatest(data);
      toast.success(`Done: ${data.passRate}% passed (${data.passed}/${data.total})`);
      load();
    } catch (e) {
      toast.error("Test run failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const categories = {};
  (latest?.suites || []).forEach((s) => {
    (categories[s.category] = categories[s.category] || []).push(s);
  });

  return (
    <TabPageLayout
      title="Daily Tests"
      subtitle="Real tests against the live system — runs automatically throughout the day via the ops cron"
      actions={
        <LayoutActionButton icon={Play} loading={running} disabled={running} onClick={runAll} data-testid="run-all-tests-btn">
          {running ? "Running…" : "Run All Tests"}
        </LayoutActionButton>
      }
    >
      <div className="space-y-6" data-testid="master-health-panel">
        {/* Summary banner */}
      {latest ? (
        <div
          className={`rounded-xl border p-6 ${latest.failed > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}
          data-testid="health-summary-banner"
        >
          <div className="flex flex-wrap items-center gap-8">
            <div>
              <p className={`text-5xl font-bold ${latest.failed > 0 ? "text-red-600" : "text-green-600"}`}>
                {latest.passRate}%
              </p>
              <p className="text-sm text-gray-600 mt-1">tests passed</p>
            </div>
            <div className="grid grid-cols-3 gap-6 text-center">
              <div><p className="text-2xl font-semibold text-gray-900">{latest.passed}</p><p className="text-xs text-gray-500">passed</p></div>
              <div><p className={`text-2xl font-semibold ${latest.failed > 0 ? "text-red-600" : "text-gray-900"}`}>{latest.failed}</p><p className="text-xs text-gray-500">failed</p></div>
              <div><p className={`text-2xl font-semibold ${latest.warnings > 0 ? "text-amber-600" : "text-gray-900"}`}>{latest.warnings}</p><p className="text-xs text-gray-500">warnings</p></div>
            </div>
            <div className="ml-auto text-right text-sm text-gray-500">
              <p className="flex items-center gap-1 justify-end"><Clock className="w-3.5 h-3.5" /> {fmtWhen(latest.finishedAt)}</p>
              <p className="mt-1">{Math.round((latest.durationMs || 0) / 1000)}s · {latest.triggeredBy === "cron" ? "scheduled" : "manual"}</p>
              <p className="mt-1 flex items-center gap-1 justify-end text-xs"><Bell className="w-3 h-3" /> SMS + email on daily run</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
          No runs yet — hit <strong>Run All Tests</strong> to get your first report.
        </div>
      )}

      {/* Data backup status */}
      {backup && (
        <div
          className={`rounded-xl border p-4 flex flex-wrap items-center gap-4 ${backup.stale ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}
          data-testid="backup-status-card"
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${backup.stale ? "bg-red-100" : "bg-emerald-50"}`}>
            <DatabaseBackup className={`w-5 h-5 ${backup.stale ? "text-red-600" : "text-emerald-600"}`} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">Data Backup</p>
              <span
                className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${backup.stale ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}
                data-testid="backup-freshness-badge"
              >
                {backup.lastBackupAt ? (backup.stale ? "STALE" : "FRESH") : "NONE YET"}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-testid="backup-last-run">
              {backup.lastBackupAt
                ? <>Last backed up <strong className="text-gray-700">{backup.hoursAgo < 1 ? "less than an hour ago" : `${Math.round(backup.hoursAgo)}h ago`}</strong> · {fmtWhen(backup.lastBackupAt)} · {backup.tableCount} tables · {backup.totalRows?.toLocaleString()} rows · {(backup.compressedBytes / 1024 / 1024).toFixed(1)} MB · stored off-site</>
                : "No backup found in S3 yet."}
            </p>
          </div>
          <button
            onClick={runBackup}
            disabled={backingUp}
            className="text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            data-testid="backup-now-btn"
          >
            {backingUp ? "Backing up…" : "Backup Now"}
          </button>
        </div>
      )}

      {/* Suites grouped by category */}
      {Object.entries(categories).map(([cat, suites]) => (
        <div key={cat}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{cat}</h3>
          <div className="space-y-2">
            {suites.map((s) => {
              const meta = statusMeta[s.status] || statusMeta.passed;
              const Icon = meta.icon;
              const open = expanded[s.key];
              return (
                <div key={s.key} className={`rounded-lg border bg-white ${s.status !== "passed" ? meta.bg : "border-gray-200"}`}>
                  <button
                    className="w-full flex items-center gap-3 p-3 text-left"
                    onClick={() => setExpanded((p) => ({ ...p, [s.key]: !p[s.key] }))}
                    data-testid={`suite-row-${s.key}`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${meta.color}`} />
                    <span className="font-medium text-gray-900 flex-1">{s.label}</span>
                    {s.error && <span className="text-xs text-red-500 mr-2">error: {s.error.slice(0, 40)}</span>}
                    <span className="text-sm text-gray-500">{s.passed}/{s.total}</span>
                    <span className="text-xs text-gray-400 w-14 text-right">{Math.round((s.durationMs || 0) / 1000)}s</span>
                    {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </button>
                  {open && (
                    <div className="px-11 pb-3 text-sm text-gray-600">
                      {s.failedTests?.length > 0 ? (
                        <ul className="list-disc space-y-1">
                          {s.failedTests.map((t, i) => <li key={i} className="text-red-600">{t}</li>)}
                        </ul>
                      ) : (
                        <p className="text-green-600">All {s.total} tests passed.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* History */}
      {history.length > 1 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Recent Runs</h3>
          <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
            {history.map((r) => (
              <div key={r.id} className="flex items-center gap-4 px-4 py-2.5 text-sm" data-testid={`history-row-${r.id}`}>
                <span className={`font-semibold w-12 ${r.failed > 0 ? "text-red-600" : "text-green-600"}`}>{r.passRate}%</span>
                <span className="text-gray-600">{r.passed}/{r.total} passed</span>
                {r.failed > 0 && <span className="text-red-500">{r.failed} failed</span>}
                {r.warnings > 0 && <span className="text-amber-500">{r.warnings} warnings</span>}
                <span className="ml-auto text-gray-400">{r.triggeredBy === "cron" ? "scheduled" : "manual"} · {fmtWhen(r.startedAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </TabPageLayout>
  );
};

export default MasterHealthPanel;
